/**
 * Payment context for managing boost balance, subscriptions, and auto top-up.
 * Boost balance is fetched from nara-core via BFF (getPaymentStatus).
 * Stripe billing (subscriptions, payment methods) remains user-scoped via Firestore.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callable } from "./callable";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import {
  getCurrentWorkspaceId,
  subscribeToWorkspaceId,
} from "./workspaceStore";

// Auto top-up settings interface
interface AutoTopUpSettings {
  enabled: boolean;
  threshold: number;
  topUpPriceId: string;
  topUpAmount: number;
  monthlyBudget: number;
  currentMonthSpend: number;
}

// Payment state interface
interface PaymentState {
  boostBalance: number;
  /** Contract workspace: members see no Boost balance and no payment page. */
  billingHidden: boolean;
  subscriptionTier: string;
  subscriptionPriceId: string | null;
  subscriptionStatus: string | null;
  monthlyBoostAllocation: number;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  autoTopUp: AutoTopUpSettings | null;
  hasPaymentMethod: boolean;
  loading: boolean;
  error: string | null;
}

// Boost transaction record
interface BoostTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  feature?: string;
  createdAt: string;
}

// Payment actions interface
interface PaymentActions {
  // Subscription management
  createSubscription: (priceId: string) => Promise<{ clientSecret: string | null; status: string }>;
  cancelSubscription: () => Promise<void>;
  reactivateSubscription: () => Promise<void>;

  // Boost purchases
  purchaseBoosts: (priceId: string) => Promise<{ clientSecret: string }>;


  // Auto top-up
  updateAutoTopUp: (settings: Partial<AutoTopUpSettings>) => Promise<void>;

  // Payment methods
  createSetupIntent: () => Promise<{ clientSecret: string }>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  removePaymentMethod: (paymentMethodId: string) => Promise<void>;

  // Transaction history
  getTransactions: (
    limit?: number,
    startAfter?: string
  ) => Promise<{ transactions: BoostTransaction[]; hasMore: boolean }>;

  // Refresh
  refreshStatus: () => Promise<void>;
}

// Combined context type
type PaymentContextType = PaymentState &
  PaymentActions & {
    /** True once getPaymentStatus answered for the current workspace, so billingHidden is known. */
    billingStatusLoaded: boolean;
  };

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// Initial state
/** What getPaymentStatus answers; lives in React Query so a write elsewhere refetches it. */
interface PaymentStatus {
  billingHidden?: boolean;
  boostBalance: number;
  subscriptionTier: string;
  subscriptionPriceId: string | null;
  subscriptionStatus: string | null;
  monthlyBoostAllocation: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  autoTopUp: AutoTopUpSettings | null;
  hasPaymentMethod: boolean;
}

/** The part of PaymentState the live Firestore user document owns. */
type UserDocState = Pick<
  PaymentState,
  "subscriptionTier" | "subscriptionStatus" | "monthlyBoostAllocation" | "currentPeriodEnd" | "hasPaymentMethod"
>;

const initialState: PaymentState = {
  boostBalance: 0,
  billingHidden: false,
  subscriptionTier: "free",
  subscriptionPriceId: null,
  subscriptionStatus: null,
  monthlyBoostAllocation: 0,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  autoTopUp: null,
  hasPaymentMethod: false,
  loading: true,
  error: null,
};

export function PaymentProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [userDoc, setUserDoc] = useState<Partial<UserDocState>>({});
  const [workspaceId, setWorkspaceId] = useState<string | null>(
    getCurrentWorkspaceId()
  );

  // Subscribe to workspace ID changes
  useEffect(() => {
    const unsubscribe = subscribeToWorkspaceId((id) => {
      setWorkspaceId(id);
    });
    // Sync in case it changed before subscription
    setWorkspaceId(getCurrentWorkspaceId());
    return unsubscribe;
  }, []);

  // Cloud function references
  const createSubscriptionFn = callable<
    { priceId: string; workspaceId: string },
    { subscriptionId: string; clientSecret: string | null; status: string }
  >("createSubscription");

  const cancelSubscriptionFn = callable<void, { success: boolean }>("cancelSubscription");

  const reactivateSubscriptionFn = callable<void, { success: boolean }>("reactivateSubscription");

  const purchaseBoostsFn = callable<
    { priceId: string; workspaceId: string },
    { clientSecret: string; paymentIntentId: string }
  >("purchaseBoosts");

  const updateAutoTopUpFn = callable<
    { workspaceId: string; enabled: boolean; threshold?: number; topUpPriceId?: string; monthlyBudget?: number },
    { success: boolean; settings: AutoTopUpSettings }
  >("updateAutoTopUp");

  const createSetupIntentFn = callable<void, { clientSecret: string }>("createSetupIntent");

  const setDefaultPaymentMethodFn = callable<
    { paymentMethodId: string },
    { success: boolean }
  >("setDefaultPaymentMethod");

  const removePaymentMethodFn = callable<
    { paymentMethodId: string },
    { success: boolean }
  >("removePaymentMethod");

  const getBoostTransactionsFn = callable<
    { limit?: number; startAfter?: string },
    { transactions: BoostTransaction[]; hasMore: boolean }
  >("getBoostTransactions");

  const getPaymentStatusFn = callable<{ workspaceId?: string }, PaymentStatus>("getPaymentStatus");

  // Workspace boost balance from BFF (nara-core backed). A write anywhere refetches it.
  const uid = user?.uid;
  const statusQuery = useQuery({
    queryKey: ["paymentStatus", uid, workspaceId],
    enabled: !!user && !!workspaceId,
    queryFn: async () => {
      try {
        return (await getPaymentStatusFn({ workspaceId: workspaceId! })).data;
      } catch (error) {
        console.error("Error fetching boost balance:", error);
        throw error;
      }
    },
  });
  const status = statusQuery.data;
  // A failed load leaves it false: the server still refuses purchases and ledger reads for a hidden workspace.
  const billingHidden = status?.billingHidden === true;
  // Optimistic edit of the cached status; the next refetch overwrites it with the server's.
  const patchStatus = useCallback(
    (patch: Partial<PaymentStatus>) =>
      queryClient.setQueryData<PaymentStatus>(
        ["paymentStatus", uid, workspaceId],
        (prev) => prev && { ...prev, ...patch }
      ),
    [queryClient, uid, workspaceId]
  );

  // Listen to user document for subscription/payment info (Firestore — not migrated)
  useEffect(() => {
    if (!user) {
      setUserDoc({});
      return;
    }

    const userDocRef = doc(db, "users", user.uid);
    const unsubscribeUser = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setUserDoc({
            subscriptionTier: data.subscriptionTier ?? "free",
            subscriptionStatus: data.subscriptionStatus ?? null,
            monthlyBoostAllocation: data.monthlyBoostAllocation ?? 0,
            currentPeriodEnd: data.currentPeriodEnd
              ? data.currentPeriodEnd.toDate()
              : null,
            hasPaymentMethod: !!data.defaultPaymentMethodId,
          });
        }
      },
      (error) => {
        console.error("Error listening to user document:", error);
      }
    );

    return () => unsubscribeUser();
  }, [user]);

  // Action implementations
  const createSubscription = useCallback(
    async (priceId: string) => {
      const wsId = getCurrentWorkspaceId();
      if (!wsId) throw new Error("No active workspace");
      const result = await createSubscriptionFn({ priceId, workspaceId: wsId });
      return {
        clientSecret: result.data.clientSecret,
        status: result.data.status,
      };
    },
    [createSubscriptionFn]
  );

  const cancelSubscription = useCallback(async () => {
    await cancelSubscriptionFn();
    patchStatus({ cancelAtPeriodEnd: true });
  }, [cancelSubscriptionFn, patchStatus]);

  const reactivateSubscription = useCallback(async () => {
    await reactivateSubscriptionFn();
    patchStatus({ cancelAtPeriodEnd: false });
  }, [reactivateSubscriptionFn, patchStatus]);

  const purchaseBoosts = useCallback(
    async (priceId: string) => {
      const wsId = getCurrentWorkspaceId();
      if (!wsId) throw new Error("No active workspace");
      const result = await purchaseBoostsFn({ priceId, workspaceId: wsId });
      return { clientSecret: result.data.clientSecret };
    },
    [purchaseBoostsFn]
  );

  const updateAutoTopUp = useCallback(
    async (settings: Partial<AutoTopUpSettings>) => {
      const wsId = getCurrentWorkspaceId();
      if (!wsId) throw new Error("No active workspace");
      const result = await updateAutoTopUpFn({
        workspaceId: wsId,
        enabled: settings.enabled ?? false,
        threshold: settings.threshold,
        topUpPriceId: settings.topUpPriceId,
        monthlyBudget: settings.monthlyBudget,
      });
      patchStatus({ autoTopUp: result.data.settings });
    },
    [updateAutoTopUpFn, patchStatus]
  );

  const createSetupIntent = useCallback(async () => {
    const result = await createSetupIntentFn();
    return { clientSecret: result.data.clientSecret };
  }, [createSetupIntentFn]);

  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      await setDefaultPaymentMethodFn({ paymentMethodId });
      setUserDoc((prev) => ({ ...prev, hasPaymentMethod: true }));
      patchStatus({ hasPaymentMethod: true });
    },
    [setDefaultPaymentMethodFn, patchStatus]
  );

  const removePaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      await removePaymentMethodFn({ paymentMethodId });
    },
    [removePaymentMethodFn]
  );

  const getTransactions = useCallback(
    async (limit?: number, startAfter?: string) => {
      // Members of a contract workspace may not read the ledger; the server refuses anyway.
      if (billingHidden) return { transactions: [], hasMore: false };
      const result = await getBoostTransactionsFn({ limit, startAfter });
      return result.data;
    },
    [getBoostTransactionsFn, billingHidden]
  );

  const { refetch: refetchStatus, isEnabled: statusEnabled } = statusQuery;
  const refreshStatus = useCallback(async () => {
    if (!statusEnabled) return;
    await refetchStatus({ throwOnError: true });
  }, [refetchStatus, statusEnabled]);

  const value: PaymentContextType = {
    boostBalance: status?.boostBalance ?? 0,
    billingHidden,
    subscriptionTier: userDoc.subscriptionTier ?? status?.subscriptionTier ?? initialState.subscriptionTier,
    subscriptionPriceId: status?.subscriptionPriceId ?? null,
    subscriptionStatus: userDoc.subscriptionStatus ?? status?.subscriptionStatus ?? null,
    monthlyBoostAllocation: userDoc.monthlyBoostAllocation ?? status?.monthlyBoostAllocation ?? 0,
    currentPeriodEnd:
      userDoc.currentPeriodEnd ?? (status?.currentPeriodEnd ? new Date(status.currentPeriodEnd) : null),
    cancelAtPeriodEnd: status?.cancelAtPeriodEnd ?? false,
    autoTopUp: status?.autoTopUp ?? null,
    hasPaymentMethod: userDoc.hasPaymentMethod ?? status?.hasPaymentMethod ?? false,
    loading: !user || (!!workspaceId && statusQuery.isPending),
    error: statusQuery.isError ? "Failed to load boost balance" : null,
    billingStatusLoaded: !!workspaceId && (statusQuery.isSuccess || statusQuery.isError),
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    purchaseBoosts,
    updateAutoTopUp,
    createSetupIntent,
    setDefaultPaymentMethod,
    removePaymentMethod,
    getTransactions,
    refreshStatus,
  };

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
}
